import {fabric} from 'fabric';
import {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {UploadButton} from '~/CreateFabricGreetingCard/components/UploadButton/UploadButton';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {useIsMediumAndUp} from '~/common/hooks/useMatchQuery';

import './Uyo.sass';

export const Uyo = ({pristineUyo, eventId, className}) => {
  const {canvas} = useContext(FabricContext);
  const [styles, setStyles] = useState({});
  const dispatch = useDispatch();
  const isMediumAndUp = useIsMediumAndUp();

  const selectUyo = useCallback(
    (e) => {
      const uyoImage = canvas
        .getObjects()
        .find((obj) => obj.type === 'uYOImage' && !!obj._originalObject);
      const rect = canvas.getObjects().find((obj) => obj.type === 'rect');

      const Mouse = canvas.getPointer(e);

      if (uyoImage && rect?.containsPoint(Mouse, null, true)) {
        canvas.setActiveObject(uyoImage).renderAll();
      }
    },
    [canvas]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const positionBtn = useCallback((obj, canvas) => {
    const {x: left, y: top} = obj.getCenterPoint();
    const zoom = isMediumAndUp ? 0.5 : 0.25;

    const leftOffset = isMediumAndUp ? 110 : 68;
    const topOffset = isMediumAndUp ? 20 : 30;

    setStyles({
      left: `${left * zoom - leftOffset}px`,
      top: `${top * zoom - topOffset}px`,
      position: 'absolute',
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const overrideFindTarget = useCallback(() => {
    fabric.util.addListener(canvas.upperCanvasEl, 'mousedown', selectUyo);
    fabric.util.addListener(canvas.upperCanvasEl, 'touchstart', selectUyo);

    const FindTarget = function (e, skipGroup) {
      if (this.skipTargetFind) {
        return;
      }
      const ignoreZoom = true;
      const pointer = this.getPointer(e, ignoreZoom);
      const activeObject = this._activeObject;
      const aObjects = this.getActiveObjects();
      let activeTarget;
      let activeTargetSubs;
      const isTouch = fabric.util.isTouchEvent(e);
      const shouldLookForActive = (aObjects.length > 1 && !skipGroup) || aObjects.length === 1;

      this.targets = [];

      if (shouldLookForActive && activeObject._findTargetCorner(pointer, isTouch)) {
        return activeObject;
      }

      if (
        aObjects.length > 1 &&
        !skipGroup &&
        activeObject === this._searchPossibleTargets([activeObject], pointer)
      ) {
        return activeObject;
      }
      if (aObjects.length === 1 && !!this._searchPossibleTargets([activeObject], pointer)) {
        return activeObject;
      }
      let target = this._searchPossibleTargets(this._objects, pointer);
      if (e[this.altSelectionKey] && target && activeTarget && target !== activeTarget) {
        target = activeTarget;
        this.targets = activeTargetSubs;
      }
      return target;
    };
    canvas.findTarget = FindTarget.bind(canvas);
  }, [canvas]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const imageUploadedCallback = useCallback(
    (uyoImage) => {
      overrideFindTarget();

      canvas.setActiveObject(uyoImage).renderAll();

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {pristineUyo: false},
      });
    },
    [canvas, selectUyo]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (canvas) {
      const uyoImage = canvas.getObjects().find((obj) => obj.type === 'uYOImage');

      const rect = canvas.getObjects().find((obj) => obj.type === 'rect');
      const textbox = canvas.getObjects().filter((obj) => obj.type === 'textbox');

      const positionBtnHandler = () => positionBtn(uyoImage, canvas);
      if ((!rect && uyoImage) || uyoImage?.isError) {
        positionBtnHandler();
        window.addEventListener('resize', positionBtnHandler);
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {pristineUyo: true},
        });
      } else if (rect && uyoImage) {
        uyoImage.hasControls = false;
        uyoImage._originalObject = rect.toJSON();
        overrideFindTarget();
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {pristineUyo: false},
        });
      }

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {hasTextbox: !!textbox.length},
      });

      return () => {
        window.removeEventListener('resize', positionBtnHandler);
      };
    }
  }, [!!canvas, canvas?.getObjects()?.length]);

  if (!pristineUyo) return null;

  return (
    <UploadButton
      styles={styles}
      className={`uyo-upload-btn ${className}`}
      callback={imageUploadedCallback}
      eventId={eventId}
    />
  );
};
